import { useNewTranslation } from 'hooks'
import RectangleButton from 'particles/Button/RectangleButton'
import Modal from 'particles/Modal'
import styled from 'particles/themes/styled'
import React, { useMemo, useState } from 'react'
import { CampaignTemplateState, OBJECTIVE, ObjectiveType, setTemplateFilter } from 'state/campaignTemplates'
import TemplateCard from './TemplateCard'
import { useDispatch } from 'react-redux'
import Text from 'particles/Text'

interface AllTemplateModalProps {
    templates: CampaignTemplateState[]
    selectedTemplateUuid?: string
    currentTemplateUuid?: string
    setSelectedTemplateUuid: (id: string) => void
}

const AllTemplateModal = ({
    templates,
    setSelectedTemplateUuid,
    selectedTemplateUuid,
    currentTemplateUuid,
}: AllTemplateModalProps) => {
    const [t] = useNewTranslation()
    const dispatch = useDispatch()
    const [isModalOpen, setIsModalOpen] = useState(false)
    const templateByObjectives = useMemo(() => {
        return templates.reduce(
            (acc, curr) => ({
                ...acc,
                [curr.objective]: [...(acc[curr.objective] || []), curr],
            }),
            {} as Record<number, CampaignTemplateState[]>,
        )
    }, [templates])
    const objectiveInOrder = [
        OBJECTIVE.awareness,
        OBJECTIVE.appPromotion,
        OBJECTIVE.traffic,
        OBJECTIVE.credibility,
        OBJECTIVE.content,
    ]
    const objectiveLabel = {
        [OBJECTIVE.awareness]: t('campaign.objectiveModal.awarenessButton.title'),
        [OBJECTIVE.appPromotion]: t('campaign.objectiveModal.appPromotionButton.title'),
        [OBJECTIVE.traffic]: t('campaign.objectiveModal.trafficButton.title'),
        [OBJECTIVE.credibility]: t('campaign.objectiveModal.credibilityButton.title'),
        [OBJECTIVE.content]: t('campaign.objectiveModal.contentButton.title'),
    } as const

    const openModal = () => {
        setIsModalOpen(true)
    }

    const closeModal = () => {
        setIsModalOpen(false)
    }

    return (
        <>
            <RectangleButton
                onClick={openModal}
                type="button"
                form="text"
                label={t('campaign.template.step1.templateSelection.seeAllTemplateButton.text')}
            />
            {isModalOpen && (
                <Modal
                    size="l"
                    title={t('campaign.template.allTemplatesModal.title')}
                    hasIcon={false}
                    hasSecondaryButton={false}
                    hasButtonSet={false}
                    subtext={t('campaign.template.step1.templateSelection.subTitle')}
                    onClose={() => {
                        closeModal()
                    }}
                    onConfirm={() => {
                        closeModal()
                    }}
                >
                    <Container>
                        {objectiveInOrder.map(objective => {
                            if (!templateByObjectives[objective]) {
                                return null
                            }

                            return (
                                <>
                                    <Text scale="H4">{objectiveLabel[objective]}</Text>
                                    <div className="all-template-list">
                                        {templateByObjectives[objective].map((template: CampaignTemplateState) => (
                                            <TemplateCard
                                                key={template.uuid}
                                                title={t(template.name)}
                                                description={t(template.description)}
                                                subTitle={t(
                                                    'campaign.template.step1.templateSelection.templateCard.durationParticipants.text',
                                                    {
                                                        duration: template.duration,
                                                        min: template.kpi_range.min,
                                                        max: template.kpi_range.max,
                                                    },
                                                )}
                                                icon={
                                                    Object.entries(OBJECTIVE).find(
                                                        ([key, val]) => val === template.objective,
                                                    )?.[0] as ObjectiveType
                                                }
                                                isSelected={template.uuid === selectedTemplateUuid}
                                                isCurrentTemplate={template.uuid === currentTemplateUuid}
                                                onClick={() => {
                                                    setSelectedTemplateUuid(template.uuid)
                                                    dispatch(
                                                        setTemplateFilter({
                                                            templateUuid: template.uuid,
                                                        }),
                                                    )
                                                }}
                                                showButton={
                                                    template.uuid == selectedTemplateUuid &&
                                                    template.uuid !== currentTemplateUuid
                                                }
                                                dataTestId={`test-${template.name}`}
                                            />
                                        ))}
                                    </div>
                                </>
                            )
                        })}
                    </Container>
                </Modal>
            )}
        </>
    )
}

export default AllTemplateModal

const Container = styled.div`
    display: flex;
    flex-direction: column;
    gap: ${({ theme }) => theme.spacing[400]};

    .all-template-list {
        display: grid;
        grid-template-columns: repeat(auto-fill, 377px);
        gap: ${({ theme }) => theme.spacing[600]};
    }
`
